import { ReactElement, ReactNode, useMemo } from 'react';
import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { FANCY_CONTAINER_CLASS_NAME } from '@components/icon-fancy-container/icon-fancy-container';
import { IconProps } from '@components/icons';
import { IconDocument } from '@components/icons/document';
import { IconDrag } from '@components/icons/drag';
import { IconForm } from '@components/icons/form';
import { IconView } from '@components/icons/icon-view';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconStarSolid } from '@components/icons/star-solid';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconWorkspace } from '@components/icons/workspace';
import { Menu } from '@components/menu';
import { Nav } from '@components/nav';
import { NavGroupCollapse } from '@components/nav-group-collapse/nav-group-collapse';
import { useNavContext } from '@components/nav/nav';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';
import { pxToRem } from '@utils/converters';

import { useUserOption } from 'api/authentication/hooks';
import { useFavoritesOrdered } from 'api/favorites/hooks';
import { FavoriteContentType } from 'api/favorites/requests';
import { routePaths } from 'ui/authenticated/navigation';

import { RemoveFavoriteMenuItem } from '../favorites/favorites';

const ICON_SIZE = 16;

const contentTypeMap: Record<
  FavoriteContentType,
  {
    getLink: (props: { workspaceId: string; shortId: string; journeyId?: string }) => string;
    icon: ReactElement<IconProps>;
  }
> = {
  journey: {
    getLink: ({ shortId = '', workspaceId = '' }) => {
      return generatePath(routePaths.journeyDetails, {
        workspaceId,
        journeyId: shortId,
        '*': null,
      });
    },
    icon: <IconJourneyItem size={ICON_SIZE} />,
  },
  documenttemplate: {
    getLink: ({ shortId, workspaceId }) => {
      return generatePath(routePaths.worksspaceDocumentDetail, {
        workspaceId,
        documentId: shortId,
        '*': null,
      });
    },
    icon: <IconDocument size={ICON_SIZE} />,
  },
  journeyform: {
    getLink: ({ shortId, workspaceId, journeyId }) => {
      if (!journeyId) {
        return '';
      }
      return (
        generatePath(routePaths.editJourney, {
          workspaceId,
          journeyId,
          '*': 'journey',
        }) + `?openJourneyForm=${shortId}`
      );
    },
    icon: <IconForm size={ICON_SIZE} />,
  },
  journeyview: {
    getLink: ({ shortId, workspaceId, journeyId }) => {
      if (!journeyId) {
        return '';
      }
      return (
        generatePath(routePaths.journeyDetails, {
          workspaceId,
          journeyId,
          '*': 'travellers',
        }) + `?viewId=${shortId}`
      );
    },
    icon: <IconView size={ICON_SIZE} />,
  },
  companytravellerview: {
    getLink: ({ shortId }) => {
      return generatePath(routePaths.travellersList, { '*': 'list' }) + `?viewId=${shortId}`;
    },
    icon: <IconView size={ICON_SIZE} />,
  },
};

const expandedStore = createSelectionStore({ persistKey: 'favorites-nav-state' });

export const Favorites = () => {
  const { t } = useScopedTranslate(locale);
  const favorites = useFavoritesOrdered();
  const { update } = useUserOption('navbarFavoritesOrder', []);
  const { isOpen } = useNavContext();

  const links = useMemo(() => {
    if (!favorites?.length) {
      return [];
    }

    return favorites.map(
      ({
        name,
        contentType,
        workspaceId,
        objectShortId,
        shortId,
        journeyShortId,
        workspaceName,
        journeyName,
      }) => {
        const { getLink, icon } = contentTypeMap[contentType as FavoriteContentType];
        const href = getLink({ workspaceId, shortId: objectShortId, journeyId: journeyShortId });

        const tooltipContent = workspaceName ? (
          <FavoriteTooltipContent
            workspaceName={workspaceName}
            journeyName={journeyName}
            name={name}
            contentType={contentType as FavoriteContentType}
            itemIcon={icon}
          />
        ) : undefined;

        return {
          href,
          icon: (
            <IconFancyContainer
              size="sm"
              icon={icon}
              hoverIcon={<IconDrag size={ICON_SIZE} color="white100" />}
              variant="gray"
            />
          ),
          label: name,
          tooltip: tooltipContent
            ? { content: tooltipContent, placement: 'right' as const }
            : undefined,
          id: shortId,
          isActive: false,
          iconRight: (
            <Menu
              trigger={IconButton}
              triggerProps={{
                size: 'sm',
                variant: 'ghost-purple',
                icon: <IconMoreVertical size={ICON_SIZE} />,
              }}
            >
              <RemoveFavoriteMenuItem shortId={shortId} />
            </Menu>
          ),
        };
      }
    );
  }, [favorites]);

  if (!favorites?.length) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Stack justify="center" align="center" style={{ padding: `${pxToRem(8)} 0` }}>
          <IconStarSolid size={ICON_SIZE} color="elementOrange100" />
        </Stack>
      )}
      <Container>
        <NavGroupCollapse
          id="main-navbar-favorites"
          label={t('favorites')}
          links={links}
          toggleGroup={expandedStore.toggle}
          expandedSelectionStore={expandedStore}
          onDragEnd={update.mutate}
        />
        <Nav.Separator />
      </Container>
    </>
  );
};

const TooltipRow = ({ children }: { children: ReactNode }) => (
  <Stack orientation="horizontal" space={1} align="center">
    {children}
  </Stack>
);

const FavoriteTooltipContent = ({
  workspaceName,
  journeyName,
  name,
  contentType,
  itemIcon,
}: {
  workspaceName: string;
  journeyName?: string;
  name: string;
  contentType: FavoriteContentType;
  itemIcon: ReactElement<IconProps>;
}) => {
  const showItemRow = contentType !== 'journey';

  return (
    <Stack space={1} style={{ textAlign: 'left' }}>
      <TooltipRow>
        <IconWorkspace size={ICON_SIZE} />
        <span>{workspaceName}</span>
      </TooltipRow>
      {journeyName && (
        <TooltipRow>
          <IconSubIndicator size={ICON_SIZE} />
          <IconJourneyItem size={ICON_SIZE} />
          <span>{journeyName}</span>
        </TooltipRow>
      )}
      {showItemRow && (
        <TooltipRow>
          <IconSubIndicator size={ICON_SIZE} />
          {itemIcon}
          <span>{name}</span>
        </TooltipRow>
      )}
    </Stack>
  );
};

const Container = styled.div({
  [`.${FANCY_CONTAINER_CLASS_NAME}`]: {
    height: pxToRem(ICON_SIZE),
    width: pxToRem(ICON_SIZE),
  },
});

const locale = {
  en: {
    favorites: 'Favourites',
  },
};
